<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useUserStore } from '@/stores/user';
import {
    listBvpnTokens,
    createBvpnToken,
    revokeBvpnToken,
    type BvpnToken,
    type CreatedBvpnToken,
} from '@/api/bvpn-tokens';

const user = useUserStore();
const tokens = ref<BvpnToken[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

const showCreate = ref(false);
const newLabel = ref('');
const creating = ref(false);
const justCreated = ref<CreatedBvpnToken | null>(null);
const copyState = ref<'idle' | 'copied'>('idle');

const isInternal = computed(() => !!user.identity?.isInternal);

async function load() {
    loading.value = true;
    error.value = null;
    try {
        tokens.value = await listBvpnTokens();
    } catch (e) {
        error.value = (e as Error).message || 'Failed to load tokens';
    } finally {
        loading.value = false;
    }
}

function openCreate() {
    newLabel.value = '';
    justCreated.value = null;
    copyState.value = 'idle';
    showCreate.value = true;
}

function closeCreate() {
    showCreate.value = false;
    newLabel.value = '';
    justCreated.value = null;
}

async function create() {
    const label = newLabel.value.trim();
    if (!label) return;
    creating.value = true;
    try {
        justCreated.value = await createBvpnToken(label);
        await load();
    } catch (e) {
        error.value = (e as Error).message || 'Failed to create token';
    } finally {
        creating.value = false;
    }
}

const mobaUrl = computed(() => {
    if (!justCreated.value) return '';
    const origin = window.location.origin; // https://supportvpn.bazeport.net in prod
    return `${origin}/api/sites.mxtsessions?token=${encodeURIComponent(justCreated.value.token)}`;
});
const mobaCopyState = ref<'idle' | 'copied'>('idle');

// Install commands kept here (not in the template) so the literal lives in
// one place — `bvpn-cli/dist-templates/install.{sh,ps1}` is the source of
// truth for the served scripts; this is just the URL surface users copy.
type InstallKey = 'linux' | 'windows';
const INSTALL_CMDS: Record<InstallKey, string> = {
    linux: 'curl -fsSL https://supportvpn.bazeport.net/bvpn-dist/install.sh | bash',
    windows: 'iwr https://supportvpn.bazeport.net/bvpn-dist/install.ps1 -UseBasicParsing | iex',
};
const installCopied = ref<InstallKey | null>(null);
async function copyInstall(which: InstallKey) {
    try {
        await navigator.clipboard.writeText(INSTALL_CMDS[which]);
        installCopied.value = which;
        setTimeout(() => {
            if (installCopied.value === which) installCopied.value = null;
        }, 1500);
    } catch { /* clipboard may be blocked */ }
}

async function copy(text: string, which: 'token' | 'moba') {
    try {
        await navigator.clipboard.writeText(text);
        if (which === 'token') {
            copyState.value = 'copied';
            setTimeout(() => { copyState.value = 'idle'; }, 1500);
        } else {
            mobaCopyState.value = 'copied';
            setTimeout(() => { mobaCopyState.value = 'idle'; }, 1500);
        }
    } catch {
        // Clipboard may be blocked in some browsers — user can still select manually.
    }
}

function copyToken() {
    if (justCreated.value) void copy(justCreated.value.token, 'token');
}
function copyMobaUrl() {
    if (mobaUrl.value) void copy(mobaUrl.value, 'moba');
}

async function revoke(t: BvpnToken) {
    if (!confirm(`Revoke "${t.label}"? Any bvpn CLI using this token will be signed out.`)) return;
    try {
        await revokeBvpnToken(t.id);
        await load();
    } catch (e) {
        error.value = (e as Error).message || 'Failed to revoke';
    }
}

function fmt(dt: string | null): string {
    if (!dt) return '—';
    return new Date(dt).toLocaleString();
}

onMounted(() => {
    if (isInternal.value) load();
});
</script>

<template>
    <div class="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <h1 class="text-2xl font-semibold mb-3">CLI access tokens</h1>

        <div v-if="!isInternal" class="bg-bp-panel border border-bp-border rounded p-4 text-sm">
            CLI access tokens are for internal staff only.
        </div>

        <template v-else>
            <p class="text-bp-muted text-sm mb-6">
                Personal access tokens for the <code class="text-bp-text">bvpn</code> CLI. Each
                machine should have its own token — revoke from here if a laptop is lost or
                rotated out. The plaintext token is shown only at creation time.
            </p>

            <div v-if="error" class="bg-red-900/30 border border-red-700 text-red-200 px-4 py-2 rounded mb-4 text-sm">
                {{ error }}
            </div>

            <div class="bg-bp-panel border border-bp-border rounded">
                <div class="flex items-center justify-between px-4 py-3 border-b border-bp-border">
                    <span class="text-sm text-bp-muted">{{ tokens.length }} active</span>
                    <button
                        type="button"
                        class="px-3 py-1.5 rounded bg-brand-bright-blue text-white text-sm hover:bg-brand-bright-blue/90"
                        @click="openCreate">
                        Generate new token
                    </button>
                </div>

                <div v-if="loading" class="px-4 py-6 text-sm text-bp-muted">Loading…</div>
                <div v-else-if="tokens.length === 0" class="px-4 py-6 text-sm text-bp-muted">
                    No active tokens. Generate one to use the bvpn CLI.
                </div>
                <table v-else class="w-full text-sm">
                    <thead class="bg-bp-panel-2 text-bp-muted">
                        <tr>
                            <th class="text-left px-4 py-2 font-medium">Label</th>
                            <th class="text-left px-4 py-2 font-medium">Created</th>
                            <th class="text-left px-4 py-2 font-medium">Last used</th>
                            <th class="text-left px-4 py-2 font-medium">From IP</th>
                            <th class="text-right px-4 py-2 font-medium">&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="t in tokens" :key="t.id" class="border-t border-bp-border">
                            <td class="px-4 py-2 text-bp-text">{{ t.label }}</td>
                            <td class="px-4 py-2 text-bp-muted">{{ fmt(t.createdAt) }}</td>
                            <td class="px-4 py-2 text-bp-muted">{{ fmt(t.lastUsedAt) }}</td>
                            <td class="px-4 py-2 text-bp-muted">{{ t.lastUsedIp ?? '—' }}</td>
                            <td class="px-4 py-2 text-right">
                                <button
                                    type="button"
                                    class="text-red-400 hover:text-red-300 text-xs"
                                    @click="revoke(t)">
                                    Revoke
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <p class="text-bp-muted text-xs mt-4">
                On a new machine, run any session command (e.g. <code>bvpn ssh</code>). The CLI
                will prompt for a token if one isn't already stored. Or run <code>bvpn login</code>
                to re-paste an existing one.
            </p>

            <div class="bg-bp-panel border border-bp-border rounded mt-6 px-4 py-3">
                <h2 class="font-semibold text-sm mb-2">Install or update bvpn</h2>
                <p class="text-bp-muted text-xs mb-3">
                    Run the installer on each machine that needs bvpn. It downloads
                    the right binary, wires up shell completion, and offers to install
                    <code>fzf</code> for interactive picking.
                </p>

                <p class="text-bp-muted text-[0.65rem] uppercase tracking-wide mb-1">Linux / macOS</p>
                <div class="flex items-stretch gap-2 mb-3">
                    <code class="flex-1 bg-bp-bg border border-bp-border rounded px-3 py-2 break-all
                                 font-mono text-xs text-bp-text">{{ INSTALL_CMDS.linux }}</code>
                    <button type="button" @click="copyInstall('linux')"
                            class="px-3 rounded border border-bp-border text-bp-muted hover:text-bp-text text-xs whitespace-nowrap">
                        {{ installCopied === 'linux' ? 'Copied!' : 'Copy' }}
                    </button>
                </div>

                <p class="text-bp-muted text-[0.65rem] uppercase tracking-wide mb-1">Windows (PowerShell)</p>
                <div class="flex items-stretch gap-2">
                    <code class="flex-1 bg-bp-bg border border-bp-border rounded px-3 py-2 break-all
                                 font-mono text-xs text-bp-text">{{ INSTALL_CMDS.windows }}</code>
                    <button type="button" @click="copyInstall('windows')"
                            class="px-3 rounded border border-bp-border text-bp-muted hover:text-bp-text text-xs whitespace-nowrap">
                        {{ installCopied === 'windows' ? 'Copied!' : 'Copy' }}
                    </button>
                </div>

                <p class="text-bp-muted text-xs mt-3">
                    Already installed? bvpn self-updates once per day; run
                    <code>bvpn update</code> to force an immediate update and
                    session-cache refresh.
                </p>
            </div>

            <div class="bg-bp-panel border border-bp-border rounded mt-6 px-4 py-3">
                <h2 class="font-semibold text-sm mb-1">Use MobaXterm instead?</h2>
                <p class="text-bp-muted text-xs">
                    Generate a token above — the dialog will show a live MobaXterm
                    URL you can paste into <em>Settings → Manage existing shared sessions</em>.
                    Same sessions as the bvpn CLI, refreshed by MobaXterm on every load.
                </p>
            </div>
        </template>

        <!-- Create / show-once modal -->
        <div v-if="showCreate"
             class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-30"
             @click.self="closeCreate">
            <div class="bg-bp-panel border border-bp-border rounded w-full max-w-md p-5">
                <h2 class="font-semibold mb-3">
                    {{ justCreated ? 'Token created' : 'Generate new token' }}
                </h2>

                <template v-if="!justCreated">
                    <label class="block text-sm text-bp-muted mb-1">Label</label>
                    <input
                        v-model="newLabel"
                        type="text"
                        maxlength="64"
                        placeholder="e.g. espen-thinkpad"
                        class="w-full px-3 py-2 bg-bp-bg border border-bp-border rounded text-sm mb-2"
                        @keyup.enter="create" />
                    <p class="text-bp-muted text-xs mb-4">
                        Suggestion: use the machine's hostname so you can tell tokens apart.
                    </p>
                    <div class="flex justify-end gap-2">
                        <button
                            type="button"
                            class="px-3 py-1.5 rounded text-bp-muted hover:text-bp-text text-sm"
                            @click="closeCreate">
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="px-3 py-1.5 rounded bg-brand-bright-blue text-white text-sm
                                   hover:bg-brand-bright-blue/90 disabled:opacity-50"
                            :disabled="creating || !newLabel.trim()"
                            @click="create">
                            {{ creating ? 'Creating…' : 'Create' }}
                        </button>
                    </div>
                </template>

                <template v-else>
                    <p class="text-sm text-bp-text mb-2">
                        Copy this token now — it won't be shown again.
                    </p>
                    <div class="flex items-stretch gap-2 mb-3">
                        <div class="flex-1 bg-bp-bg border border-bp-border rounded px-3 py-2 break-all
                                    font-mono text-xs text-bp-text">
                            {{ justCreated.token }}
                        </div>
                        <button
                            type="button"
                            class="px-3 rounded border border-bp-border text-bp-muted hover:text-bp-text text-xs whitespace-nowrap"
                            @click="copyToken">
                            {{ copyState === 'copied' ? 'Copied!' : 'Copy' }}
                        </button>
                    </div>
                    <p class="text-bp-muted text-xs mb-4">
                        Paste into <code>bvpn</code> when prompted, or run
                        <code>bvpn login</code> on the target machine to set it.
                    </p>

                    <!-- Live URL for MobaXterm shared-sessions slot. Includes
                         the plaintext token, so this is the same one-shot
                         exposure as the token itself — closing the dialog
                         means re-generating to get the URL again. -->
                    <p class="text-sm text-bp-text mb-2">
                        Or paste this live URL into MobaXterm:
                    </p>
                    <div class="flex items-stretch gap-2 mb-3">
                        <div class="flex-1 bg-bp-bg border border-bp-border rounded px-3 py-2 break-all
                                    font-mono text-xs text-bp-text">
                            {{ mobaUrl }}
                        </div>
                        <button
                            type="button"
                            class="px-3 rounded border border-bp-border text-bp-muted hover:text-bp-text text-xs whitespace-nowrap"
                            @click="copyMobaUrl">
                            {{ mobaCopyState === 'copied' ? 'Copied!' : 'Copy URL' }}
                        </button>
                    </div>
                    <ol class="list-decimal list-inside text-bp-muted text-xs mb-3 space-y-1">
                        <li>
                            <em>Settings → Manage existing shared sessions</em>
                            (not <em>Create shared sessions</em>) → <em>Add</em>
                        </li>
                        <li>Tree folder name: <code>Netbird</code></li>
                        <li>Backend protocol: <code>http / https</code></li>
                        <li>Sessions file URL: paste the URL above</li>
                        <li>Click <em>Save</em></li>
                    </ol>
                    <p class="text-bp-muted text-xs mb-4">
                        Revoking the token here disables both the CLI and this URL.
                    </p>

                    <div class="flex justify-end gap-2">
                        <button
                            type="button"
                            class="px-3 py-1.5 rounded bg-brand-bright-blue text-white text-sm
                                   hover:bg-brand-bright-blue/90"
                            @click="closeCreate">
                            Done
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>
